<template>

<div class="bar-right" :class="{'is-compact': sharedState.sort || sharedState.layout || sharedState.text}" v-click-outside="onOutsideClick" ref="bar">

	<template v-if="!$isMobile">

		<slot></slot>

	</template>

	<core-button v-if="$isMobile" :style="{marginTop: marginTop}" v-on:click="onToggleClick" theme="filter" :icon="(is.expanded) ? 'remove' : 'ellipsis-v'" :iconOnly="true" class="bar-right-toggle" />

	<div class="bar-right-expand" :style="{marginTop: marginTop}" v-touch:swipe.right="onSwipe" :class="{'is-active': is.expanded}" v-if="$isMobile">

		<div class="bar-right-title">Actions</div>

		<slot></slot>

	</div>

</div>

</template>

<script>

import vClickOutside from 'v-click-outside'
import sharedState from './state'

var BUFFER_PX = 16
var TOGGLE_COOLDOWN_MS = 200

// Order matters — when overflow is detected, the first non-compact stage
// in this list is collapsed; un-compacting walks the list in reverse.
// Sort is the longest text and least essential at a glance, search is
// the most useful when always visible, so it collapses last.
var STAGES = ['sort', 'layout', 'text']

export default {

	directives: {
		clickOutside: vClickOutside.directive
	},

	data: function() {

		return {
			is: {
				expanded: false
			},
            scrollY: 0,
			sharedState: sharedState,
			stageTriggerWidths: { sort: 0, layout: 0, text: 0 }
		}

	},

    computed: {

        marginTop: function() {

            var offset = (0 - this.window.scrollY)

            if (offset < -40) offset = -40

            return offset.toString() + 'px'

        }

    },

	mounted: function() {

		this.measureBound = this.scheduleMeasure.bind(this)
		this.measureRaf = 0
		this.safetyTimers = []
		this.lastToggleAt = 0
		this.lastWindowWidth = window.innerWidth
		this.observersActive = false

		this.onWindowResize = this.handleWindowResize.bind(this)
		window.addEventListener('resize', this.onWindowResize)

		if (!this.$isMobile) this.activateObservers()

	},

	watch: {

		// Crossing the mobile/desktop boundary swaps the slotted DOM
		// between two v-if branches, which leaves any prior observers
		// pointing at detached nodes and (in the mobile→desktop case)
		// means observers were never set up at all. Reset cleanly so
		// the desktop side gets fresh wiring and the stages start from
		// a known state.
		'$isMobile': function(isMobile) {

			this.deactivateObservers()
			this.resetStages()

			if (!isMobile) {

				this.$nextTick(function() {
					this.activateObservers()
				}.bind(this))

			}

		}

	},

	beforeDestroy: function() {

		this.deactivateObservers()
		if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize)
		if (this.measureRaf) cancelAnimationFrame(this.measureRaf)

	},

	methods: {

		activateObservers: function() {

			if (this.observersActive || !this.$refs.bar) return

			this.observersActive = true

			if (window.__BAR_DEBUG) console.log('[bar-right] activate observers')

			if (typeof ResizeObserver !== 'undefined') {

				this.resizeObserver = new ResizeObserver(this.measureBound)
				this.resizeObserver.observe(this.$refs.bar)

				var inner = this.$refs.bar.parentElement
				if (inner) this.resizeObserver.observe(inner)

				this.observeChildren()

			}

			if (typeof MutationObserver !== 'undefined') {

				this.mutationBound = this.onMutation.bind(this)
				this.mutationObserver = new MutationObserver(this.mutationBound)
				this.mutationObserver.observe(this.$refs.bar, {
					childList: true,
					subtree: true,
					characterData: true
				})

			}

			// Safety nets: bar-right's children often render their final
			// width only after async data settles (sort text from Vuex,
			// dropdown labels, etc). ResizeObserver on bar-right itself
			// won't fire if content grows past its container without
			// changing bar-right's own border-box size, so we re-measure
			// at a few intervals after mount to catch late content.
			this.scheduleSafety(50)
			this.scheduleSafety(250)
			this.scheduleSafety(1000)
			this.scheduleSafety(2500)

		},

		deactivateObservers: function() {

			this.observersActive = false

			if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null }
			if (this.mutationObserver) { this.mutationObserver.disconnect(); this.mutationObserver = null }
			if (this.safetyTimers) { this.safetyTimers.forEach(clearTimeout); this.safetyTimers = [] }

		},

		resetStages: function() {

			sharedState.sort = false
			sharedState.layout = false
			sharedState.text = false
			this.stageTriggerWidths = { sort: 0, layout: 0, text: 0 }
			this.lastToggleAt = 0

		},

		handleWindowResize: function() {

			// Big window jumps (responsive breakpoint crossings, devtools
			// open/close, browser zoom) invalidate the trigger widths we
			// recorded at a different viewport — clear them so we re-measure
			// from a clean baseline rather than staying stuck in a compact
			// state computed for a different size.
			var width = window.innerWidth
			if (Math.abs(width - this.lastWindowWidth) > 100) this.resetStages()
			this.lastWindowWidth = width

			this.scheduleMeasure()

		},

		scheduleMeasure: function() {

			if (this.measureRaf) return

			this.measureRaf = requestAnimationFrame(function() {

				this.measureRaf = 0
				this.measure()

			}.bind(this))

		},

		scheduleSafety: function(ms) {

			var t = setTimeout(this.measureBound, ms)
			this.safetyTimers.push(t)

		},

		observeChildren: function() {

			if (!this.resizeObserver || !this.$refs.bar) return

			var children = this.$refs.bar.children
			for (var i = 0; i < children.length; i++) {

				try { this.resizeObserver.observe(children[i]) } catch (e) { /* already observed or detached */ }

			}

		},

		onMutation: function() {

			// Pick up newly mounted children for child-level resize
			// tracking — late slot content (e.g. tags loaded async)
			// otherwise wouldn't be observed and content growth
			// would slip past the measure pass.
			this.observeChildren()

			// Mutations within the cooldown window are almost certainly
			// caused by our own compact toggle re-rendering Sort/Layout/
			// Text — reacting to them would oscillate compact on/off.
			// Why: flipping a stage flag swaps slot content, which fires
			// this observer; if we reset stages here we'd loop.
			if (Date.now() - this.lastToggleAt < TOGGLE_COOLDOWN_MS) return

			this.scheduleMeasure()

		},

		setStage: function(stage, value) {

			if (sharedState[stage] === value) return

			sharedState[stage] = value
			this.lastToggleAt = Date.now()

			// Schedule a verify-measure after the cooldown — without it,
			// a resize that comes to rest mid-compaction would leave us
			// stuck (mutations during cooldown are suppressed, so no
			// follow-up measure would otherwise fire).
			var self = this
			setTimeout(function() { self.scheduleMeasure() }, TOGGLE_COOLDOWN_MS + 50)

		},

		measure: function() {

			// Mobile uses a fixed toggle button + expand panel rather than
			// inline slot content — there's nothing on the bar to measure
			// for overflow, so skip entirely.
			if (this.$isMobile) return

			var bar = this.$refs.bar
			if (!bar) {
				if (window.__BAR_DEBUG) console.log('[bar-right] no bar ref')
				return
			}

			var inner = bar.parentElement
			if (!inner) {
				if (window.__BAR_DEBUG) console.log('[bar-right] no inner parent')
				return
			}

			var leftSibling = bar.previousElementSibling
			var barRect = bar.getBoundingClientRect()
			var innerRect = inner.getBoundingClientRect()
			var available = inner.clientWidth

			var overflow = false
			var reason = ''

			if (barRect.right > innerRect.right + 1) { overflow = true; reason = 'bar.right > inner.right' }
			if (barRect.right > window.innerWidth + 1) { overflow = true; reason += ' | bar past window' }
			if (inner.scrollWidth > available + 1) { overflow = true; reason += ' | inner overflow' }
			if (leftSibling && leftSibling.scrollWidth > leftSibling.clientWidth + 1) { overflow = true; reason += ' | left squeezed' }

			if (window.__BAR_DEBUG) {
				console.log('[bar-right] measure', {
					stages: { sort: sharedState.sort, layout: sharedState.layout, text: sharedState.text },
					overflow: overflow,
					reason: reason,
					barRight: barRect.right,
					innerRight: innerRect.right,
					innerClient: inner.clientWidth,
					innerScroll: inner.scrollWidth,
					winInner: window.innerWidth,
					barScroll: bar.scrollWidth,
					barClient: bar.clientWidth,
					leftScroll: leftSibling ? leftSibling.scrollWidth : null,
					leftClient: leftSibling ? leftSibling.clientWidth : null,
					triggers: this.stageTriggerWidths
				})
			}

			if (overflow) {

				// Compact the next stage in priority order. Each stage
				// records the *inner* container's natural content width
				// (bar-left + bar-right combined) at the moment it was
				// collapsed; un-compact when available exceeds that.
				// Why inner.scrollWidth instead of bar.scrollWidth: the
				// bar shares space with bar-left, so available is always
				// much larger than bar-right alone. Comparing available
				// to bar.scrollWidth would un-compact immediately, then
				// re-overflow, then re-compact — a tight oscillation.
				for (var i = 0; i < STAGES.length; i++) {

					var stage = STAGES[i]
					if (!sharedState[stage]) {

						this.stageTriggerWidths[stage] = inner.scrollWidth
						this.setStage(stage, true)
						return

					}

				}

				// All stages already compact and still overflowing —
				// nothing more we can do.
				return

			}

			// Not overflowing. Walk stages in reverse priority and try
			// to un-compact the most-recently-collapsed one if there's
			// now room for what it originally wanted.
			for (var j = STAGES.length - 1; j >= 0; j--) {

				var s = STAGES[j]
				if (sharedState[s]) {

					var trigger = this.stageTriggerWidths[s]
					if (trigger && available > trigger + BUFFER_PX) {

						this.setStage(s, false)

					}

					// Only consider one stage per measure pass; the next
					// measure (after the cooldown) will handle the next.
					return

				}

			}

		},

		onSwipe: function() {

			if (this.window.is.mobile) {

				this.is.expanded = false

			}

		},

		onToggleClick: function() {

			this.is.expanded = !this.is.expanded

		},

		onOutsideClick: function() {

			this.is.expanded = false

		}

	}

}

</script>

<style scoped>

.bar-right {
	display: flex;
	justify-content: flex-end;
	flex-shrink: 0;
	height: 40px;
	padding: 12px 20px;
	min-width: 0;
}

.is-smaller .bar-right {
	padding: 12px 20px;
}

.is-tablet .bar-right {
	padding: 12px 10px;
}

.is-mobile .bar-right {
	padding: 4px 0px;
}

.bar-right-toggle {
	z-index: 3;
}

.bar-right-toggle.button.theme-filter {
	top: 40px;
}

.bar-right-toggle.button.theme-filter.is-expanded {
	top: 00px;
}

.bar-right-dropdown,
.bar-right-expand {
	position: fixed;
	z-index: 1;
    right: -240px;
    top: 40px;
	background-color: #2b5870;
	width: 200px;
    bottom: 0px;
    box-shadow: 0 7px 29px 0 rgba(100, 100, 111, .2);
	transition: right 100ms linear;
	padding: 50px 0px 0px 0px;
	overflow: auto;
	user-select: none;
}

.bar-right-dropdown {
	z-index: 2;
}

.bar-right-dropdown.is-active,
.bar-right-expand.is-active {
	right: 0px;
}

.bar-right-expand >>> .theme-filter {
	width: 100%!important;
	max-width: 100%!important;
	box-shadow: none;
	background-color: #2b5870;
	color: #fff;
	border-bottom: 1px solid rgba(0, 0, 0, 0.05);
	border-radius: 0px!important;
	height: 40px!important;
	line-height: 40px!important;
	margin: 0px!important;
}

.bar-right-expand > div:nth-child(2) {
	border-top: 1px solid rgba(0, 0, 0, 0.05);
}

#app.is-mobile:not(.is-listing) .bar.has-scrolled .bar-right-toggle {
    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
}

.bar-right-title {
	font-size: 14px;
	position: absolute;
	font-weight: 500;
	left: 8px;
	color: #fff;
	letter-spacing: 1px;
	text-transform: uppercase;
	top: 10px;
	line-height: 32px;
}

.is-mobile .bar-right-title {
	color: #fff;
    font-family: 'Barlow', sans-serif;
	text-transform: initial;
    letter-spacing: 1px;
    font-weight: 700;
    font-size: 16px;
}


</style>
